/*
 * r16m (2026-10-20), design D:/NecroWork/r16m-batch-evidence/DESIGN.md 2.9 / guide L6: the live hint under #1217 "DeviceIds Running
 * Accs Of This Batch CSV" on the slot settings page, on the lib/blankSourceWarning.js pattern - a pure function, never a block. The
 * server's own save check (BatchSettingsCheck) is the real one: it normalises the separators, refuses an empty #1217 with #1216 on
 * from V3 (400, nothing saved), and returns its warnings as `notes`, which the page already shows as a toast. This card says the same
 * thing BEFORE the save, while the value is typed.
 *
 * Shown only while #1216 "Operate Accs As Combined Batches" is on (with it off, #1217 is not read). The checks:
 *   EMPTY        no phone listed - the server refuses the V3 save (V1 stores the slot's own phone instead);
 *   SEPARATORS   new lines, tabs or ';' between the ids - the server stores them as ", " (the worker now splits on them too);
 *   UNKNOWN      an id that is not one of this account's phones (the Devices list);
 *   OWN_MISSING  the slot's own phone (#7 "Select Shared Device", igacc.deviceid) is not listed - the new worker adds it, an older
 *                worker does not read that phone's file;
 *   DEFAULT      still the placeholder "device1, device2, device3";
 *   TOO_LONG     over 255 characters once the separators are ", " - the server refuses the V3 save (400, nothing saved; V1 keeps
 *                the value stored before);
 *   ID_TOO_LONG  one id over 100 characters (no phone has one; the server's batch_device holds 100) - the server refuses the V3 save
 *                (NSB-r16m-batch c0c82bd, BatchSettingsCheck / DeviceSet.MAX_DEVICE_CHARS);
 * and, with the account's batch records (GET /uiapi/follow-back/batches; review 2026-10-20, design 2.9's last two warnings):
 *   NOT_MEMBER / DETACHED / TWO_BATCHES  the phones touch a batch this slot is not a member of (or two): engages nobody in batch mode;
 *   DIFFERS      a member's list and its batch's phones differ.
 * Nothing here defines a setting: the ids are V1's (#7, #1216, #1217), the field names come from FIELD_METADATA.
 */

import { batchName } from './followBackView';

export const BATCH_1217_TAG = '[R16M-1217]';

export const BATCH_IDS = Object.freeze({ ownDevice: 7, combined: 1216, devices: 1217 });

export const DEFAULT_1217 = 'device1, device2, device3';
export const MAX_1217_LENGTH = 255;
export const MAX_DEVICE_ID_LENGTH = 100;

/** A checkbox value as the page holds it: true, 'true' (any case), 1, '1'. */
const ticked = (v) => {
  if (v === true || v === 1) return true;
  if (v === null || v === undefined || v === false) return false;
  const s = String(v).trim().toLowerCase();
  return s === 'true' || s === '1';
};

/**
 * The #1217 tokens as the server and the worker split them (design 2.4): on any run of ',' ';' or white space, trimmed, empties
 * dropped, de-duplicated case-insensitively (the first spelling kept). Returns { tokens, keys (lower-cased), normalised (", ") }.
 */
export function split1217(value) {
  const seen = new Set();
  const tokens = [];
  String(value === null || value === undefined ? '' : value).split(/[,;\s]+/).forEach((t) => {
    const tok = t.trim();
    if (!tok) return;
    const k = tok.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    tokens.push(tok);
  });
  return { tokens, keys: tokens.map((t) => t.toLowerCase()), normalised: tokens.join(', ') };
}

/**
 * null, or the hint: { title, severity: 'error' | 'warn', items: [{ code, text }], logLine }.
 * byId: the page's values by setting id ({7, 1216, 1217}; a missing 1216 or 1217 = not known -> null). devices: the account's phone
 * ids (the Devices list), or null when not loaded (then UNKNOWN is not checked). Pure.
 */
export function batch1217Warning(byId, devices, opts = null) {
  const v = byId || {};
  const I = BATCH_IDS;
  if (!Object.prototype.hasOwnProperty.call(v, I.combined) || !Object.prototype.hasOwnProperty.call(v, I.devices)) return null;
  if (!ticked(v[I.combined])) return null;
  const raw = v[I.devices] === null || v[I.devices] === undefined ? '' : String(v[I.devices]);
  const { tokens, keys, normalised } = split1217(raw);
  const own = v[I.ownDevice] ? String(v[I.ownDevice]).trim() : '';
  const items = [];
  if (!tokens.length) {
    items.push({
      code: 'EMPTY',
      text: `#1217 is empty: list every phone of this batch, including this slot's own${own ? ` (#7: ${own})` : ' (#7)'}. `
        + 'The server refuses this save from here (V1 stores the slot\'s own phone instead).',
    });
  } else {
    if (/[;\r\n\t]/.test(raw)) {
      items.push({ code: 'SEPARATORS', text: 'The phones are separated by new lines, tabs or ";": the server stores them separated by ", ".' });
    }
    if (Array.isArray(devices)) {
      const known = new Set(devices.map((d) => String(d).trim().toLowerCase()).filter(Boolean));
      const unknown = tokens.filter((t) => !known.has(t.toLowerCase()));
      if (unknown.length) {
        items.push({
          code: 'UNKNOWN',
          text: `${unknown.join(', ')} ${unknown.length === 1 ? 'is' : 'are'} not one of this account's phones (Devices). Check the spelling: `
            + 'a phone that is not listed right is not part of the batch.',
          ids: unknown,
        });
      }
    }
    if (own && !keys.includes(own.toLowerCase())) {
      items.push({
        code: 'OWN_MISSING',
        text: `This slot's own phone (#7: ${own}) is not listed. The new worker adds it; an older worker does not read that phone's list.`,
      });
    }
    if (split1217(DEFAULT_1217).normalised.toLowerCase() === normalised.toLowerCase()) {
      items.push({ code: 'DEFAULT', text: `#1217 is still the default "${DEFAULT_1217}": put this batch's real phone ids here.` });
    }
    if (normalised.length > MAX_1217_LENGTH) {
      items.push({
        code: 'TOO_LONG',
        text: `${normalised.length} characters once separated by ", " - over ${MAX_1217_LENGTH}: the server refuses this save from here `
          + '(nothing is saved; V1 keeps the value stored before).',
      });
    }
    const longIds = tokens.filter((t) => t.length > MAX_DEVICE_ID_LENGTH);
    if (longIds.length) {
      items.push({
        code: 'ID_TOO_LONG',
        text: `"${longIds[0].slice(0, 24)}…" is one id of ${longIds[0].length} characters - over ${MAX_DEVICE_ID_LENGTH}, and no phone has such an id: `
          + 'separate the ids with ", ". The server refuses this save from here (nothing is saved).',
      });
    }
  }
  // the batch notes need a list to compare (an empty #1217 is EMPTY already: the server refuses that save)
  if (tokens.length) batchItems(items, { keys: own && !keys.includes(own.toLowerCase()) ? [...keys, own.toLowerCase()] : keys, ...(opts || {}) });
  if (!items.length) return null;
  const refused = items.filter((i) => ['EMPTY', 'TOO_LONG', 'ID_TOO_LONG'].includes(i.code));
  const blocking = items.filter((i) => ['NOT_MEMBER', 'DETACHED', 'TWO_BATCHES'].includes(i.code));
  const severity = refused.length || blocking.length ? 'error' : 'warn';
  let title = `#1216 is on: check #1217 (${items.length} ${items.length === 1 ? 'note' : 'notes'})`;
  if (items.some((i) => i.code === 'EMPTY')) title = '#1216 is on, but #1217 lists no phone';
  else if (refused.length) title = '#1217 cannot be saved like this';
  else if (blocking.length) title = '#1216 is on, but in batch mode this slot engages nobody';
  const batchId = (items.find((i) => i.batch) || {}).batch || null;
  return {
    title,
    batch: batchId,
    severity,
    items,
    logLine: `${BATCH_1217_TAG} 1216 on, 1217 "${normalised.slice(0, 80)}" (${tokens.length} phone${tokens.length === 1 ? '' : 's'}): `
      + `${items.map((i) => i.code + (i.ids ? `(${i.ids.join(',')})` : '')).join(', ')} - warned, saving not blocked here`,
  };
}

const lc = (s) => String(s).trim().toLowerCase();

/**
 * The batch-aware notes (design 2.9 "not a member of the batch its phones belong to" / "differs from the batch's device set"; the
 * server's BatchResolver rules), from GET /uiapi/follow-back/batches (normalised). `keys`: the slot's device set D (its #1217 phones
 * plus its own #7, lower-cased). `slot`: the slot's name. `admin`: the list holds EVERY account's batches (no owner field), and phone
 * ids are per account, so an admin gets these notes only for a batch that names this slot (member, pending or detached).
 *   NOT_MEMBER   D touches a live batch the slot is not a member of: CONFLICT, it engages nobody in batch mode until joined;
 *   DETACHED     the slot was detached from that batch: the same, until joined again (or #1216 off);
 *   TWO_BATCHES  D touches two live batches: CONFLICT, until #1217 lists one batch's phones;
 *   DIFFERS      a member's list names phones the batch does not have, or leaves out some of the batch's phones.
 */
function batchItems(items, { keys, batches, slot, admin = false } = {}) {
  if (!Array.isArray(batches) || !slot) return;
  const name = String(slot).trim();
  const live = batches.filter((b) => b && !b.retired && Array.isArray(b.devices));
  const names = (b) => b.members.some((x) => x.slot === name) || b.pending.some((x) => x.slot === name);
  const pool = admin ? live.filter(names) : live;
  if (!pool.length) return;
  const D = new Set(keys);
  const short = batchName;
  const member = pool.find((b) => b.members.some((x) => x.slot === name)) || null;
  const touched = pool.filter((b) => b.devices.some((d) => D.has(lc(d))));
  if (member) {
    const foreign = touched.filter((b) => b !== member);
    if (foreign.length) {
      const devs = foreign.flatMap((b) => b.devices.filter((d) => D.has(lc(d))).map((d) => `${d} (${short(b)})`));
      items.push({
        code: 'TWO_BATCHES',
        text: `#1217 lists ${devs.join(', ')}, a phone of another batch: in batch mode this slot engages nobody until #1217 lists only ${short(member)}'s phones.`,
      });
      return;
    }
    const batchSet = new Set(member.devices.map(lc));
    const extra = [...D].filter((d) => !batchSet.has(d));
    const missing = member.devices.filter((d) => !D.has(lc(d)));
    if (extra.length || missing.length) {
      const parts = [];
      if (extra.length) parts.push(`${extra.join(', ')} ${extra.length === 1 ? 'is' : 'are'} not one of ${short(member)}'s phones (a save does not add ${extra.length === 1 ? 'it' : 'them'}: membership and phones change only on the Follow-back page)`);
      if (missing.length) parts.push(`${short(member)} also has ${missing.join(', ')}, which this list leaves out (the new worker reads the batch's own phones anyway; an older worker reads only this list)`);
      items.push({ code: 'DIFFERS', text: `This list differs from ${short(member)}'s phones (${member.devices.join(', ')}): ${parts.join('; ')}.`, batch: member.id });
    }
    return;
  }
  if (touched.length > 1) {
    items.push({
      code: 'TWO_BATCHES',
      text: `#1217's phones belong to ${touched.map(short).join(' and ')}: in batch mode this slot engages nobody until #1217 lists one batch's phones.`,
    });
    return;
  }
  const g = touched[0] || pool.find(names) || null;
  if (!g) return;
  const detached = g.pending.some((x) => x.slot === name && x.status === 'DETACHED');
  items.push({
    code: detached ? 'DETACHED' : 'NOT_MEMBER',
    text: detached
      ? `This slot was detached from ${short(g)} (${g.devices.join(', ')}): in batch mode it engages nobody until it is joined again (Follow-back page), or #1216 is turned off.`
      : `This slot is not in ${short(g)} (${g.devices.join(', ')}): in batch mode it engages nobody until it is joined (Follow-back page), or its #1217 is fixed.`,
    batch: g.id,
  });
}

/** The page's values for #7 / #1216 / #1217 by id (the blankSourceWarning.valuesById rule: only ids the server sent, none hidden by a gate). */
export function batchValuesById(settings, loaded, fieldMetadata, hiddenIds) {
  const out = {};
  if (!settings) return out;
  const fieldFor = (id) => {
    const e = Object.entries(fieldMetadata || {}).find(([, m]) => m && m.number !== undefined && m.number !== null && Number(m.number) === id);
    return e ? e[0] : null;
  };
  Object.values(BATCH_IDS).forEach((id) => {
    if (hiddenIds && hiddenIds.has && hiddenIds.has(id)) return;
    const field = fieldFor(id);
    if (!field) return;
    if (loaded && !Object.prototype.hasOwnProperty.call(loaded, field)) return;
    out[id] = settings[field];
  });
  return out;
}
